import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { GridColDef, GridRenderCellParams } from '@mui/x-data-grid';
import { PageWrapper } from '../../styledComponents/wrappers';
import { StripedDataGrid } from '../../styledComponents/datagrid';
import {
  getReviewQueue,
  markShortReviewLine,
  reassignReviewLocation,
  reinjectReviewLine,
  resolveTransferOrphan,
  ReviewQueueOrder,
} from '../../services/fulfillment/reviewApi';

export const REVIEW_QUEUE_ROUTE = '/fulfillment/review';

// One DataGrid row per reviewable item (an excepted/QC-failed line, or a transfer-bin orphan),
// flattened from the order-grouped projection. siblingSummary carries the partial-order context.
interface ReviewRow {
  id: string;
  kind: 'line' | 'orphan';
  orderName: string;
  reason: string;
  detail: string;
  siblingSummary: string;
  lineItemGid?: string;
  associationId?: number;
}

type PendingAction =
  | { type: 'mark-short'; row: ReviewRow }
  | { type: 'resolve-orphan'; row: ReviewRow }
  | { type: 'reassign'; row: ReviewRow };

export const flatten = (orders: ReviewQueueOrder[]): ReviewRow[] => {
  const rows: ReviewRow[] = [];
  for (const order of orders) {
    const orderName = order.shopifyOrderName ?? order.shopifyOrderGid;
    const siblingSummary = order.siblingLineStates
      .map(s => `${s.sku ?? s.shopifyLineItemGid}: ${s.status}`)
      .join(', ');
    for (const li of order.lineItems) {
      rows.push({
        id: `line-${li.lineItemStatusId}`,
        kind: 'line',
        orderName,
        reason: li.reason,
        detail: [li.sku, li.notes].filter(Boolean).join(' — '),
        siblingSummary,
        lineItemGid: li.shopifyLineItemGid,
      });
    }
    for (const o of order.orphans) {
      rows.push({
        id: `orphan-${o.associationId}`,
        kind: 'orphan',
        orderName,
        reason: o.reason,
        detail: o.note ?? '',
        siblingSummary,
        associationId: o.associationId,
      });
    }
  }
  return rows;
};

const ReviewQueue: React.FC = () => {
  const [orders, setOrders] = useState<ReviewQueueOrder[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [pending, setPending] = useState<PendingAction | null>(null);
  const [binInput, setBinInput] = useState('');
  const [actionLoading, setActionLoading] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    getReviewQueue()
      .then(setOrders)
      .catch(() => setError('Failed to load the review queue.'))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const rows = useMemo(() => flatten(orders), [orders]);

  const runAction = async (fn: () => Promise<{ ok: true } | { error: string }>) => {
    setActionLoading(true);
    setError(null);
    try {
      const result = await fn();
      if ('error' in result) {
        setError(
          result.error === 'not_found' ? 'That item is no longer in the queue.' : 'Action failed.',
        );
      }
    } catch {
      setError('Action failed — you may not have permission.');
    } finally {
      setActionLoading(false);
      setPending(null);
      setBinInput('');
      load();
    }
  };

  const confirmPending = () => {
    if (!pending) return;
    if (pending.type === 'mark-short' && pending.row.lineItemGid) {
      const gid = pending.row.lineItemGid;
      return runAction(() => markShortReviewLine(gid));
    }
    if (pending.type === 'resolve-orphan' && pending.row.associationId != null) {
      const id = pending.row.associationId;
      return runAction(() => resolveTransferOrphan(id));
    }
    if (pending.type === 'reassign' && pending.row.lineItemGid) {
      const gid = pending.row.lineItemGid;
      return runAction(() => reassignReviewLocation(gid, binInput.trim()));
    }
    return undefined;
  };

  const columns: GridColDef[] = [
    { field: 'orderName', headerName: 'Order', width: 130 },
    { field: 'reason', headerName: 'Reason', width: 200 },
    { field: 'detail', headerName: 'Detail', flex: 1, minWidth: 180 },
    { field: 'siblingSummary', headerName: 'Order line states', flex: 1, minWidth: 200 },
    {
      field: 'actions',
      headerName: 'Actions',
      width: 320,
      sortable: false,
      filterable: false,
      renderCell: (params: GridRenderCellParams<ReviewRow>) => {
        const row = params.row;
        if (row.kind === 'orphan') {
          return (
            <Button
              size="small"
              color="warning"
              variant="outlined"
              disabled={actionLoading}
              onClick={() => setPending({ type: 'resolve-orphan', row })}
            >
              Resolve
            </Button>
          );
        }
        if (!row.lineItemGid) return null;
        const gid = row.lineItemGid;
        return (
          <Stack direction="row" spacing={1}>
            <Button
              size="small"
              variant="outlined"
              disabled={actionLoading}
              onClick={() => runAction(() => reinjectReviewLine(gid))}
            >
              Re-inject
            </Button>
            <Button
              size="small"
              variant="outlined"
              disabled={actionLoading}
              onClick={() => setPending({ type: 'reassign', row })}
            >
              Reassign
            </Button>
            <Button
              size="small"
              color="error"
              variant="outlined"
              disabled={actionLoading}
              onClick={() => setPending({ type: 'mark-short', row })}
            >
              Mark short
            </Button>
          </Stack>
        );
      },
    },
  ];

  return (
    <PageWrapper>
      <Typography variant="h5" gutterBottom>
        Review Queue
      </Typography>
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
      {loading ? (
        <Box display="flex" justifyContent="center" mt={4}>
          <CircularProgress />
        </Box>
      ) : rows.length === 0 ? (
        <Typography variant="body2" color="text.secondary">
          Nothing needs review. 🎉
        </Typography>
      ) : (
        <Box sx={{ height: 600, width: '100%' }}>
          <StripedDataGrid
            rows={rows}
            columns={columns}
            disableRowSelectionOnClick
            getRowId={r => r.id}
          />
        </Box>
      )}

      <Dialog open={!!pending} onClose={() => setPending(null)}>
        <DialogTitle>
          {pending?.type === 'mark-short'
            ? 'Mark line short?'
            : pending?.type === 'resolve-orphan'
            ? 'Resolve this orphan?'
            : 'Reassign location'}
        </DialogTitle>
        <DialogContent>
          {pending?.type === 'mark-short' && (
            <DialogContentText>
              This cancels the line on <strong>{pending.row.orderName}</strong>. The rest of the
              order keeps flowing. This cannot be undone.
            </DialogContentText>
          )}
          {pending?.type === 'resolve-orphan' && (
            <DialogContentText>
              This clears the transfer-bin orphan for <strong>{pending.row.orderName}</strong> from
              the queue.
            </DialogContentText>
          )}
          {pending?.type === 'reassign' && (
            <TextField
              autoFocus
              fullWidth
              label="New bin code"
              value={binInput}
              onChange={e => setBinInput(e.target.value)}
              sx={{ mt: 1 }}
            />
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPending(null)} disabled={actionLoading}>
            Cancel
          </Button>
          <Button
            variant="contained"
            color={pending?.type === 'mark-short' ? 'error' : 'primary'}
            disabled={actionLoading || (pending?.type === 'reassign' && !binInput.trim())}
            onClick={confirmPending}
          >
            {actionLoading ? <CircularProgress size={20} /> : 'Confirm'}
          </Button>
        </DialogActions>
      </Dialog>
    </PageWrapper>
  );
};

export default ReviewQueue;
